import { Injectable, inject } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { GoogleCalendarService } from './google-calendar.service';
import { GoogleAuthStatusResponse } from '../models/google-calendar.types';

/** API returns `{ connected, data: '<oauth url>' }` at the top level (same as email-calender). */
function normalizeAuthResponse(raw: unknown): GoogleAuthStatusResponse {
  if (!raw || typeof raw !== 'object') {
    return { connected: false };
  }
  const envelope = raw as Record<string, unknown>;
  const inner = envelope['data'];
  if (inner && typeof inner === 'object' && 'connected' in (inner as object)) {
    return inner as GoogleAuthStatusResponse;
  }
  return envelope as unknown as GoogleAuthStatusResponse;
}

@Injectable({ providedIn: 'root' })
export class GoogleCalendarConnectionService {
  private readonly api = inject(ApiService);
  private readonly google = inject(GoogleCalendarService);
  private readonly toast = inject(ToastrService);

  private readonly mfaEnabledSubject = new BehaviorSubject<boolean | null>(null);
  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  private readonly authUrlSubject = new BehaviorSubject<string | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly leadCalendarManageSubject = new BehaviorSubject<boolean>(false);

  readonly mfaEnabled$ = this.mfaEnabledSubject.asObservable();
  readonly connected$ = this.connectedSubject.asObservable();
  readonly authUrl$ = this.authUrlSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly leadCalendarManage$ = this.leadCalendarManageSubject.asObservable();

  get snapshot() {
    return {
      mfaEnabled: this.mfaEnabledSubject.value,
      connected: this.connectedSubject.value,
      authUrl: this.authUrlSubject.value,
    };
  }

  loadMfaStatus(): void {
    this.api.get(`/user-mfa-type/${this.api.getUserId()}`).subscribe({
      next: response => {
        this.mfaEnabledSubject.next(!!response?.data?.authentication_type);
      },
      error: () => this.mfaEnabledSubject.next(false),
    });
  }

  checkConnection(): Observable<boolean> {
    this.loadingSubject.next(true);
    return this.google.isConnected().pipe(
      map(res => normalizeAuthResponse(res)),
      tap(res => this.applyAuthStatus(res)),
      map(res => !!res.connected),
      catchError(() => {
        this.connectedSubject.next(false);
        this.authUrlSubject.next(null);
        this.loadingSubject.next(false);
        return of(false);
      })
    );
  }

  connectGoogle(): void {
    const openPopup = (url: string) => {
      const win = window.open(url, 'remmi_google_calendar', 'width=800,height=600,scrollbars=yes,resizable=yes');
      if (!win) {
        this.toast.error('Popup blocked. Allow popups for this site and try again.');
        return;
      }

      const handler = (event: MessageEvent) => {
        if (event.data === 'authenticated') {
          window.removeEventListener('message', handler);
          win.close();
          this.connectedSubject.next(true);
          this.toast.success('Gsuite now connected to Remmi');
          this.checkConnection().subscribe();
        }
      };
      window.addEventListener('message', handler, false);
    };

    const existing = this.authUrlSubject.value;
    if (existing) {
      openPopup(existing);
      return;
    }

    this.google.isConnected().subscribe({
      next: raw => {
        const res = normalizeAuthResponse(raw);
        this.applyAuthStatus(res);
        const url = this.authUrlSubject.value;
        if (url) {
          openPopup(url);
        } else if (res.connected) {
          this.toast.info('Google calendar is already connected');
        } else {
          this.toast.error('Could not load Google sign-in URL. Please try again.');
        }
      },
      error: () => this.toast.error('Could not load Google sign-in URL. Please try again.'),
    });
  }

  private applyAuthStatus(res: GoogleAuthStatusResponse): void {
    const { connected, data, leadCalendarmanage } = res;
    this.leadCalendarManageSubject.next(!!leadCalendarmanage);
    this.connectedSubject.next(!!connected);
    if (!connected && typeof data === 'string' && data.length > 0) {
      const state = this.api.getUserId();
      this.authUrlSubject.next(data.includes('state=') ? data : `${data}&state=${state}`);
    } else {
      this.authUrlSubject.next(null);
    }
    this.loadingSubject.next(false);
  }

  startWatch(): void {
    if (!this.connectedSubject.value) return;
    this.google.startWatch().subscribe();
  }

  stopWatch(): void {
    this.google.stopWatch().subscribe();
  }
}
